import _ from 'lodash';
import {
    AddressField,
    type AddressInput,
    type Maybe,
    type Scalars,
} from '../generated/types';

export type AddressFieldWithCountry = (
    | AddressField
    | AddressField[keyof AddressField]
    | 'COUNTRY'
) &
    string;

export const fields: AddressFieldWithCountry[] = [
    ...Object.values(AddressField),
    'COUNTRY',
];

export const asAddressInputKey = (
    field: AddressFieldWithCountry
): keyof AddressInput => {
    switch (field) {
        case 'COUNTRY':
            return 'countryCode';
        default:
            return _.camelCase(field) as keyof AddressInput;
    }
};

export const addressFieldMaxLength = (
    field: AddressFieldWithCountry
): number => {
    switch (field) {
        case 'COUNTRY':
            return 4;
        case AddressField.SortingCode:
        case AddressField.PostalCode:
            return 63;
        default:
            return 255;
    }
};

export const addressFieldAutoCompleteValues: Record<
    AddressFieldWithCountry,
    string
> = {
    [AddressField.Organization]: 'organization',
    [AddressField.GivenName]: 'given-name',
    [AddressField.FamilyName]: 'family-name',
    [AddressField.AdditionalName]: 'additional-name',
    [AddressField.AddressLine1]: 'address-line1',
    [AddressField.AddressLine2]: 'address-line2',
    [AddressField.AdministrativeArea]: 'address-level1',
    [AddressField.Locality]: 'address-level2',
    [AddressField.DependentLocality]: 'address-level3',
    [AddressField.PostalCode]: 'postal-code',
    [AddressField.SortingCode]: 'address-level4',
    COUNTRY: 'country',
};

export const mkAddressInput = (
    source?: Partial<AddressInput> | undefined | null,
    defaultCountryCode = 'NL'
): AddressInput => {
    source ??= {};
    return {
        countryCode: source.countryCode ?? defaultCountryCode,
        addressLine1: source.addressLine1 ?? null,
        addressLine2: source.addressLine2 ?? null,
        administrativeArea: source.administrativeArea ?? null,
        dependentLocality: source.dependentLocality ?? null,
        locality: source.locality ?? null,
        postalCode: source.postalCode ?? null,
        sortingCode: source.sortingCode ?? null,
    };
};

export const toAddressInput = (fragment: {
    // TODO - copied type to simplify country, define type somewhere else?
    __typename: 'Address';
    addressLine1?: Maybe<Scalars['String']['output']>;
    addressLine2?: Maybe<Scalars['String']['output']>;
    administrativeArea?: Maybe<Scalars['String']['output']>;
    country: { id: string; code?: string | null | undefined };
    dependentLocality?: Maybe<Scalars['String']['output']>;
    full?: Maybe<Scalars['String']['output']>;
    html?: Maybe<Scalars['HTML']['output']>;
    locality?: Maybe<Scalars['String']['output']>;
    postalCode?: Maybe<Scalars['String']['output']>;
    sortingCode?: Maybe<Scalars['String']['output']>;
}): AddressInput => ({
    ..._.pick(fragment, [
        'countryCode',
        'addressLine1',
        'addressLine2',
        'administrativeArea',
        'locality',
        'dependentLocality',
        'postalCode',
        'sortingCode',
    ]),
    countryCode: fragment?.country?.code ?? 'NL',
});
